import * as React from 'react';

import { Checkbox } from '@lightspeed/flame/Checkbox';
import { Flex, Box } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Radio } from '@lightspeed/flame/Radio';
import { Select } from '@lightspeed/flame/Select';

import { useKeyDown } from 'hooks/index';
import {
  BarcodeDisplayOptions,
  BarcodeTypes,
  BARCODE_DISPLAY_OPTIONS,
  BARCODE_TYPES,
  LabelContent,
  LabelSize,
  LABEL_CONTENT_ACTIVE_OPTIONS,
} from 'label/utils/types';

import { waitBeforeComplete } from '../utils/async';

import { BackButton } from './BackButton/BackButton';
import { useLabelPrintForm } from './hooks';
import {
  StyledLabel,
  BarcodeDisplayOptionsLabel,
  DescriptionText,
  UnstyledButton,
} from './HubLabelPrintBody.style';

const MINIMUM_RESET_DELAY = 1000;
interface AdvancedSettingsProps {
  activeBarcodeTypes: typeof BARCODE_TYPES;
  activeLabelContentOptions: typeof LABEL_CONTENT_ACTIVE_OPTIONS;
  isLoading: boolean;
  labelPrintForm: ReturnType<typeof useLabelPrintForm>;
  toggleContent: React.Dispatch<React.SetStateAction<boolean>>;
  isDefaultSettings: boolean;
  isResetting: boolean;
  setIsResetting: React.Dispatch<React.SetStateAction<boolean>>;
  submit: () => void;
}

export const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  activeBarcodeTypes,
  activeLabelContentOptions,
  isLoading,
  labelPrintForm,
  toggleContent,
  isDefaultSettings,
  isResetting,
  setIsResetting,
  submit,
}) => {
  const {
    handleSubmit,
    setValue,
    displayPrice,
    setPreviousDisplayPrice,
    barcodeDisplayOption,
    setPreviousBarcodeDisplayOption,
    barcodeType,
    setPreviousBarcodeType,
    labelContent,
    labelContentDefault,
    setPreviousLabelContent,
    labelSize,
  } = labelPrintForm;

  useKeyDown('Enter', handleSubmit(submit));

  const isSystemID = barcodeType === BarcodeTypes.SYSTEM_SKU;
  const isSmallerSizedLabel = labelSize === LabelSize.SMALL || labelSize === LabelSize.JEWELRY;

  const toggleDisplayPrice = () => {
    setValue('displayPrice', !displayPrice);
    setPreviousDisplayPrice(!displayPrice);
  };

  const handleBarcodeDisplayOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } = {} } = event;
    setValue('barcodeDisplayOption', value as BarcodeDisplayOptions);
    setPreviousBarcodeDisplayOption(value as BarcodeDisplayOptions);
  };

  const handleBarcodeTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { target: { value } = {} } = event;
    setValue('barcodeType', value as BarcodeTypes);
    setPreviousBarcodeType(value as BarcodeTypes);
  };

  const handleLabelContentOptionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { target: { value } = {} } = event;
    setValue('labelContent', value as LabelContent);
    setPreviousLabelContent(value as LabelContent);
  };

  const resetAdvancedSettings = async () => {
    if (isDefaultSettings) {
      return;
    }
    setIsResetting(true);
    await waitBeforeComplete(Date.now(), MINIMUM_RESET_DELAY, () => {
      if (barcodeType !== BarcodeTypes.SYSTEM_SKU) {
        setValue('barcodeType', BarcodeTypes.SYSTEM_SKU);
        setPreviousBarcodeType(BarcodeTypes.SYSTEM_SKU);
      }

      if (barcodeDisplayOption !== BarcodeDisplayOptions.BARCODE_AND_NUMBERS) {
        setValue('barcodeDisplayOption', BarcodeDisplayOptions.BARCODE_AND_NUMBERS);
        setPreviousBarcodeDisplayOption(BarcodeDisplayOptions.BARCODE_AND_NUMBERS);
      }

      if (!displayPrice) {
        setValue('displayPrice', true);
        setPreviousDisplayPrice(true);
      }

      setValue('labelContent', labelContentDefault);
      setPreviousLabelContent(labelContentDefault);
      setIsResetting(false);
    });
  };

  const showDescriptionText = barcodeType !== BarcodeTypes.SYSTEM_SKU;

  return (
    <Flex flexDirection="column">
      <Box mb={3}>
        <BackButton onClick={() => toggleContent(false)} />
      </Box>
      <Box mb={3}>
        <StyledLabel>Select barcode type</StyledLabel>
        {showDescriptionText ? (
          <DescriptionText data-test="description-text">
            For items that do not have the selected value, the system ID will be used as default.
          </DescriptionText>
        ) : null}
        <Select
          data-test="print-label-select-barcode-type"
          value={barcodeType}
          disabled={isLoading || isResetting}
          onChange={event => handleBarcodeTypeChange(event)}
        >
          {[{ label: 'Select', value: '', active: true }, ...activeBarcodeTypes].map(
            ({ label, value, active }) => (
              <option
                data-test={`print-label-select-barcode-type-${value}`}
                value={value}
                key={value}
                disabled={!active}
              >
                {label}
              </option>
            )
          )}
        </Select>
      </Box>

      <Box mb={1}>
        <StyledLabel>Barcode display options</StyledLabel>
        <Box mt={2} data-test="advanced-settings-barcode-options">
          {BARCODE_DISPLAY_OPTIONS.map(({ label, value }) => {
            const disabledOption =
              !isSystemID &&
              isSmallerSizedLabel &&
              value !== ('numbers' as BarcodeDisplayOptions.NUMBERS);
            const activeSelectionDisabled = barcodeDisplayOption === value && disabledOption;
            if (activeSelectionDisabled) {
              setValue('barcodeDisplayOption', BarcodeDisplayOptions.NUMBERS);
              setPreviousBarcodeDisplayOption(BarcodeDisplayOptions.NUMBERS);
            }
            return (
              <Flex mb={2} key={value}>
                <Radio
                  data-test={`advanced-settings-barcode-option-${value}`}
                  name={label}
                  id={`advanced-settings-barcode-option-${value}`}
                  checked={barcodeDisplayOption === value}
                  disabled={isLoading || disabledOption || isResetting}
                  value={value}
                  onChange={event => handleBarcodeDisplayOptionChange(event)}
                />
                <BarcodeDisplayOptionsLabel
                  className={isLoading || disabledOption || isResetting ? 'disabled' : undefined}
                  htmlFor={`advanced-settings-barcode-option-${value}`}
                >
                  {label}
                </BarcodeDisplayOptionsLabel>
              </Flex>
            );
          })}
        </Box>
      </Box>
      <Box data-test="advanced-settings-other">
        <StyledLabel>Other options</StyledLabel>
        <Box mt={2}>
          <Checkbox
            data-test="checkbox-price"
            description="Price of item will be displayed on the label"
            disabled={isLoading || isResetting}
            label="Price"
            checked={displayPrice}
            onChange={toggleDisplayPrice}
          />
        </Box>
        <Box mt={2} mb={3}>
          <StyledLabel>Additional information</StyledLabel>
          <Select
            data-test="print-label-select-label-content"
            value={labelContent === null ? LabelContent.NONE : labelContent}
            disabled={isLoading || isResetting}
            onChange={event => handleLabelContentOptionChange(event)}
          >
            {[{ label: 'Select', value: '', active: true }, ...activeLabelContentOptions].map(
              ({ label, value, active }) => (
                <option
                  data-test={`print-label-select-label-content-${value}`}
                  value={value}
                  key={value}
                  disabled={!active}
                >
                  {label} {labelContentDefault === value && '(Default)'}
                </option>
              )
            )}
          </Select>
        </Box>
      </Box>
      <Divider />
      <Box mt={2}>
        <UnstyledButton
          data-test="reset-advanced-settings-button"
          onClick={() => resetAdvancedSettings()}
          disabled={isDefaultSettings}
        >
          Reset
        </UnstyledButton>
      </Box>
    </Flex>
  );
};
